import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useRef } from 'react';
import { UserProfile, authService, isFirebaseInitialized } from '@watchlist/shared';
import { firebaseService } from '../services/firebaseService';
import { isCapacitorNative } from '../utils/platformUtils';
import { loadPersistedProfiles, persistProfile, clearPersistedProfiles, removePersistedProfile } from '../utils/profilePersistence';

interface UserProfileCache {
  [userId: string]: {
    profile: UserProfile;
    lastUpdated: number;
    isLoading: boolean;
    notFound?: boolean;
  };
}

interface UserProfileCacheContextType {
  // Get user profile (with caching and automatic refresh)
  getUserProfile: (userId: string) => UserProfile | null;

  // Get user display name with fallback
  getUserDisplayName: (userId: string, fallback?: string) => string;

  // Get user username with fallback
  getUserUsername: (userId: string, fallback?: string) => string;

  // Get user avatar with fallback
  getUserAvatar: (userId: string) => string | undefined;

  // Force refresh a user's profile
  refreshUserProfile: (userId: string) => Promise<void>;

  // Clear cache for a user
  clearUserCache: (userId: string) => void;

  // Clear all cache
  clearAllCache: () => void;

  // Check if profile is loading
  isProfileLoading: (userId: string) => boolean;

  // Preload multiple profiles
  preloadProfiles: (userIds: string[]) => Promise<void>;

  // Update a profile in cache (for immediate updates)
  updateProfileInCache: (userId: string, updates: Partial<UserProfile>) => void;
}

const UserProfileCacheContext = createContext<UserProfileCacheContextType | undefined>(undefined);

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100; // Maximum number of profiles to cache

// Synchronous tracker for in-flight requests to prevent race conditions
// This is outside React state because state updates are async and cause race conditions
const pendingFetchPromises = new Map<string, Promise<UserProfile | null>>();

interface UserProfileCacheProviderProps {
  children: ReactNode;
}

export const UserProfileCacheProvider: React.FC<UserProfileCacheProviderProps> = ({ children }) => {
  const [cache, setCache] = useState<UserProfileCache>(() => {
    const persisted = loadPersistedProfiles();
    const initial: UserProfileCache = {};
    const now = Date.now();
    for (const [userId, entry] of Object.entries(persisted)) {
      initial[userId] = {
        profile: {
          uid: entry.uid,
          displayName: entry.displayName,
          username: entry.username,
          photoURL: entry.photoURL,
          photoThumbnailURL: entry.photoThumbnailURL,
          email: '',
          favoriteGenres: [],
          privacy: { profileVisibility: 'public', showWatchlist: true, showRatings: true, showActivity: true },
          stats: { moviesWatched: 0, reviewsWritten: 0, listsCreated: 0, followersCount: 0, followingCount: 0 },
        } as UserProfile,
        lastUpdated: now,
        isLoading: false,
      };
    }
    return initial;
  });
  const cacheRef = useRef(cache);

  // Keep ref up to date
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  // Clean up old cache entries
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    setCache(prevCache => {
      const entries = Object.entries(prevCache);

      // Remove expired entries
      const validEntries = entries.filter(([_, data]) =>
        now - data.lastUpdated < CACHE_DURATION
      );

      // If still too many entries, keep only the most recent ones
      if (validEntries.length > MAX_CACHE_SIZE) {
        validEntries.sort((a, b) => b[1].lastUpdated - a[1].lastUpdated);
        validEntries.splice(MAX_CACHE_SIZE);
      }

      return Object.fromEntries(validEntries);
    });
  }, []);

  // Cleanup cache every 5 minutes
  useEffect(() => {
    const interval = setInterval(cleanupCache, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [cleanupCache]);

  const refreshUserProfile = useCallback(async (userId: string): Promise<void> => {
    // Check if Firebase is initialized before proceeding
    if (!isFirebaseInitialized()) {
      console.error('❌ UserProfileCache: Firebase not initialized yet, skipping profile refresh for:', userId);
      // Set a failed state but don't mark as loading to prevent infinite retries
      setCache(prev => ({
        ...prev,
        [userId]: {
          profile: prev[userId]?.profile || {} as UserProfile,
          lastUpdated: prev[userId]?.lastUpdated || 0,
          isLoading: false // CRITICAL: Set to false to prevent infinite retries
        }
      }));
      return;
    }

    // Validate userId before making Firebase call
    if (!userId || typeof userId !== 'string' || userId.trim() === '' || userId === 'userProfiles') {
      console.error('❌ UserProfileCache: Invalid userId provided:', userId);
      return;
    }

    // CRITICAL FIX: Check for existing pending fetch using synchronous Map (not React state)
    // This prevents race conditions where multiple calls happen before state updates
    const existingFetch = pendingFetchPromises.get(userId);
    if (existingFetch) {
      // Only log on web to reduce native console noise
      if (!isCapacitorNative()) {
        console.log('⏳ UserProfileCache: Reusing existing fetch for:', userId);
      }
      await existingFetch;
      return;
    }

    console.log('🔄 UserProfileCache: Refreshing profile for:', userId);

    // Set loading state
    setCache(prev => ({
      ...prev,
      [userId]: {
        profile: prev[userId]?.profile || {} as UserProfile,
        lastUpdated: prev[userId]?.lastUpdated || 0,
        isLoading: true
      }
    }));

    // Create the fetch promise and store it synchronously BEFORE any await
    const fetchPromise = (async (): Promise<UserProfile | null> => {
      try {
        const profile = await authService.getUserProfile(userId);

        if (profile) {
          console.log('✅ UserProfileCache: Profile refreshed for:', userId, {
            displayName: profile.displayName,
            username: profile.username
          });

          setCache(prev => ({
            ...prev,
            [userId]: {
              profile,
              lastUpdated: Date.now(),
              isLoading: false
            }
          }));
          persistProfile(userId, profile);
          return profile;
        } else {
          console.log('⚠️ UserProfileCache: No profile found for:', userId);
          setCache(prev => ({
            ...prev,
            [userId]: {
              profile: prev[userId]?.profile || {} as UserProfile,
              lastUpdated: Date.now(),
              isLoading: false,
              notFound: true,
            }
          }));
          return null;
        }
      } catch (error) {
        console.error('❌ UserProfileCache: Error refreshing profile for:', userId, error);

        // Check if this is a Firebase initialization error
        const isInitError = error instanceof Error && (
          error.message.includes('Firebase has not been initialized') ||
          error.message.includes('Expected first argument to collection()') ||
          error.message.includes('Firebase not initialized yet')
        );

        if (isInitError) {
          console.warn('⚠️ UserProfileCache: Firebase not ready, will not retry for:', userId);
        }

        // CRITICAL: Set loading to false to prevent infinite retries
        setCache(prev => ({
          ...prev,
          [userId]: {
            profile: prev[userId]?.profile || {} as UserProfile,
            lastUpdated: prev[userId]?.lastUpdated || 0,
            isLoading: false
          }
        }));
        return null;
      } finally {
        // Always clean up the pending fetch
        pendingFetchPromises.delete(userId);
      }
    })();

    // Store the promise synchronously (before any await)
    pendingFetchPromises.set(userId, fetchPromise);

    // Wait for completion
    await fetchPromise;
  }, []);

  const getUserProfile = useCallback((userId: string): UserProfile | null => {
    // Validate userId before proceeding
    if (!userId || typeof userId !== 'string' || userId.trim() === '' || userId === 'userProfiles') {
      console.error('❌ UserProfileCache: Invalid userId provided to getUserProfile:', userId);
      return null;
    }

    // Use cacheRef to avoid dependency on cache state which causes infinite loops
    const cached = cacheRef.current[userId];

    // Check if we have a valid cached profile with actual data (not just empty object)
    if (cached && !cached.isLoading && cached.profile && cached.profile.uid) {
      // Validate that the returned profile matches the requested userId
      if (cached.profile.uid !== userId) {
        console.error('🚨 UserProfileCache: Cache corruption detected!', {
          requestedUserId: userId,
          cachedProfileUid: cached.profile.uid,
          cachedDisplayName: cached.profile.displayName
        });
        // Clear corrupted cache entry asynchronously
        setTimeout(() => {
          setCache(prev => {
            const newCache = { ...prev };
            delete newCache[userId];
            return newCache;
          });
          // Trigger refresh for correct profile (only if not already loading and Firebase is ready)
          if (!cacheRef.current[userId]?.isLoading && isFirebaseInitialized()) {
            refreshUserProfile(userId);
          }
        }, 0);
        return null;
      }
      return cached.profile;
    }

    // If already loading, don't trigger another request
    if (cached?.isLoading) {
      return null;
    }

    // CRITICAL: Don't trigger refresh if Firebase is not initialized
    if (!isFirebaseInitialized()) {
      console.log('⚠️ UserProfileCache: Firebase not initialized, skipping profile load for:', userId);
      return null;
    }

    // Check if profile needs to be loaded or refreshed
    const now = Date.now();
    // Don't retry "not found" profiles within the cache window — they don't exist
    if (cached?.notFound && (now - cached.lastUpdated < CACHE_DURATION)) {
      return null;
    }
    const needsRefresh = !cached ||
      !cached.profile ||
      !cached.profile.uid ||
      (now - cached.lastUpdated > CACHE_DURATION);

    if (needsRefresh) {
      // Set loading state and trigger refresh asynchronously to avoid setState during render
      setTimeout(() => {
        // Double-check Firebase is still initialized before proceeding
        if (!isFirebaseInitialized()) {
          console.log('⚠️ UserProfileCache: Firebase not initialized at refresh time, skipping for:', userId);
          return;
        }

        // Double-check not already loading using ref
        if (cacheRef.current[userId]?.isLoading) {
          return;
        }

        setCache(prev => ({
          ...prev,
          [userId]: {
            profile: prev[userId]?.profile || {} as UserProfile,
            lastUpdated: prev[userId]?.lastUpdated || 0,
            isLoading: true
          }
        }));
        refreshUserProfile(userId);
      }, 0);
    }

    return null;
  }, [refreshUserProfile]); // Removed 'cache' from dependencies - use cacheRef instead

  const getUserDisplayName = useCallback((userId: string, fallback?: string): string => {
    const profile = getUserProfile(userId);
    const result = profile?.displayName || fallback || `User ${userId.slice(-4)}`;

    // Debug logging for collaborator display issues
    if (profile && profile.uid !== userId) {
      console.warn('🚨 UserProfileCache: Profile UID mismatch!', {
        requestedUserId: userId,
        returnedProfileUid: profile.uid,
        returnedDisplayName: profile.displayName
      });
    }

    return result;
  }, [getUserProfile]);

  const getUserUsername = useCallback((userId: string, fallback: string = 'user'): string => {
    const profile = getUserProfile(userId);
    return profile?.username || fallback;
  }, [getUserProfile]);

  const getUserAvatar = useCallback((userId: string): string | undefined => {
    const profile = getUserProfile(userId);

    // Debug logging for collaborator display issues
    if (profile && profile.uid !== userId) {
      console.warn('🚨 UserProfileCache: Avatar UID mismatch!', {
        requestedUserId: userId,
        returnedProfileUid: profile.uid,
        returnedPhotoURL: profile.photoURL
      });
    }

    return profile?.photoThumbnailURL || profile?.photoURL;
  }, [getUserProfile]);

  const isProfileLoading = useCallback((userId: string): boolean => {
    return cache[userId]?.isLoading || false;
  }, [cache]);

  const clearUserCache = useCallback((userId: string) => {
    console.log('🧹 UserProfileCache: Clearing cache for:', userId);
    setCache(prev => {
      const newCache = { ...prev };
      delete newCache[userId];
      return newCache;
    });
    removePersistedProfile(userId);
  }, []);

  const clearAllCache = useCallback(() => {
    console.log('🧹 UserProfileCache: Clearing all cache');
    setCache({});
    pendingFetchPromises.clear();
    clearPersistedProfiles();
  }, []);

  const preloadProfiles = useCallback(async (userIds: string[]): Promise<void> => {
    // Check if Firebase is initialized before proceeding
    if (!isFirebaseInitialized()) {
      console.error('❌ UserProfileCache: Firebase not initialized yet, skipping profile preload');

      // Set cache entries to prevent future retry attempts until Firebase is ready
      const validUserIds = userIds.filter(userId =>
        userId &&
        typeof userId === 'string' &&
        userId.trim() !== '' &&
        userId !== 'userProfiles'
      );

      if (validUserIds.length > 0) {
        setCache(prev => {
          const updates: UserProfileCache = {};
          validUserIds.forEach(userId => {
            updates[userId] = {
              profile: prev[userId]?.profile || {} as UserProfile,
              lastUpdated: prev[userId]?.lastUpdated || 0,
              isLoading: false // CRITICAL: Set to false to prevent retries
            };
          });
          return { ...prev, ...updates };
        });
      }

      return;
    }

    // Filter out invalid userIds before processing
    const validUserIds = userIds.filter(userId =>
      userId &&
      typeof userId === 'string' &&
      userId.trim() !== '' &&
      userId !== 'userProfiles'
    );

    if (validUserIds.length === 0) {
      console.log('🔄 UserProfileCache: No valid userIds to preload');
      return;
    }

    const now = Date.now();

    // Filter to only uncached/expired IDs, also excluding those with pending fetches
    const toLoad = validUserIds.filter(userId => {
      // Skip if already being fetched
      if (pendingFetchPromises.has(userId)) {
        return false;
      }
      const cached = cacheRef.current[userId];
      return !cached || (now - cached.lastUpdated > CACHE_DURATION);
    });

    // Also collect existing pending fetches to await
    const existingFetches = validUserIds
      .map(userId => pendingFetchPromises.get(userId))
      .filter((p): p is Promise<UserProfile | null> => p !== undefined);

    if (toLoad.length === 0) {
      // Just wait for existing fetches if any
      if (existingFetches.length > 0) {
        await Promise.all(existingFetches);
      }
      return;
    }

    console.log(`🔄 UserProfileCache: Preloading ${toLoad.length} profiles (${validUserIds.length - toLoad.length} already cached/pending)`);

    // Set loading states
    setCache(prev => {
      const updates: UserProfileCache = {};
      toLoad.forEach(userId => {
        updates[userId] = {
          profile: prev[userId]?.profile || {} as UserProfile,
          lastUpdated: prev[userId]?.lastUpdated || 0,
          isLoading: true
        };
      });
      return { ...prev, ...updates };
    });

    // Create fetch promises and store them synchronously
    const fetchPromises = toLoad.map(userId => {
      const fetchPromise = (async (): Promise<UserProfile | null> => {
        try {
          return await authService.getUserProfile(userId);
        } catch (error) {
          console.warn('⚠️ UserProfileCache: Failed to preload profile for:', userId, error);
          return null;
        } finally {
          pendingFetchPromises.delete(userId);
        }
      })();

      pendingFetchPromises.set(userId, fetchPromise);
      return fetchPromise;
    });

    // Load profiles in parallel
    const results = await Promise.all(fetchPromises);

    // Build updates map
    const cacheUpdates: UserProfileCache = {};
    results.forEach((profile, index) => {
      const userId = toLoad[index];
      if (profile) {
        cacheUpdates[userId] = { profile, lastUpdated: now, isLoading: false };
        persistProfile(userId, profile);
      } else {
        // For failed loads, set isLoading: false to prevent infinite retries
        cacheUpdates[userId] = {
          profile: cacheRef.current[userId]?.profile || {} as UserProfile,
          lastUpdated: cacheRef.current[userId]?.lastUpdated || 0,
          isLoading: false
        };
      }
    });

    // Sync cacheRef immediately so callers that await preloadProfiles() can read names
    // right away, without waiting for the useEffect that normally syncs cacheRef.
    cacheRef.current = { ...cacheRef.current, ...cacheUpdates };

    // Then schedule the React state update for reactive consumers
    setCache(prev => ({ ...prev, ...cacheUpdates }));

    // Also wait for any existing fetches
    if (existingFetches.length > 0) {
      await Promise.all(existingFetches);
    }
  }, []);

  const updateProfileInCache = useCallback((userId: string, updates: Partial<UserProfile>) => {
    console.log('🔄 UserProfileCache: Updating profile in cache for:', userId, updates);

    setCache(prev => {
      const existing = prev[userId];

      // Create a new cache entry if it doesn't exist
      if (!existing) {
        console.log('🆕 UserProfileCache: Creating new cache entry for:', userId);
        return {
          ...prev,
          [userId]: {
            profile: {
              uid: userId,
              displayName: updates.displayName || '',
              email: '',
              favoriteGenres: [],
              privacy: {
                profileVisibility: 'public',
                showWatchlist: true,
                showRatings: true,
                showActivity: true,
              },
              stats: {
                moviesWatched: 0,
                reviewsWritten: 0,
                listsCreated: 0,
                followersCount: 0,
                followingCount: 0,
              },
              createdAt: new Date() as any,
              updatedAt: new Date() as any,
              ...updates
            } as UserProfile,
            lastUpdated: Date.now(),
            isLoading: false
          }
        };
      }

      // Don't update if loading
      if (existing.isLoading) return prev;

      const newCache = {
        ...prev,
        [userId]: {
          ...existing,
          profile: {
            ...existing.profile,
            ...updates
          },
          lastUpdated: Date.now()
        }
      };

      console.log('✅ UserProfileCache: Cache updated for:', userId, 'New displayName:', newCache[userId].profile.displayName);
      persistProfile(userId, newCache[userId].profile);
      return newCache;
    });
  }, []);

  // Listen for profile update events from firebaseService
  useEffect(() => {
    const handleProfileUpdate = (event: CustomEvent) => {
      const { userId, updates } = event.detail;
      console.log('🔄 UserProfileCache: Received profile update event:', { userId, updates });

      // Immediately update the cache with the new data
      updateProfileInCache(userId, updates);

      // Also trigger a refresh to get the complete updated profile
      setTimeout(() => {
        refreshUserProfile(userId);
      }, 100);
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate as EventListener);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate as EventListener);
    };
  }, [updateProfileInCache, refreshUserProfile]);

  // Auto-load profiles when they're requested but not in cache
  useEffect(() => {
    const requestedButNotCached = Object.keys(cache).filter(userId => {
      const cached = cache[userId];
      return !cached.profile && !cached.isLoading;
    });

    if (requestedButNotCached.length > 0) {
      preloadProfiles(requestedButNotCached);
    }
  }, [cache, preloadProfiles]);

  const contextValue: UserProfileCacheContextType = React.useMemo(() => ({
    getUserProfile,
    getUserDisplayName,
    getUserUsername,
    getUserAvatar,
    refreshUserProfile,
    clearUserCache,
    clearAllCache,
    isProfileLoading,
    preloadProfiles,
    updateProfileInCache
  }), [
    getUserProfile,
    getUserDisplayName,
    getUserUsername,
    getUserAvatar,
    refreshUserProfile,
    clearUserCache,
    clearAllCache,
    isProfileLoading,
    preloadProfiles,
    updateProfileInCache
  ]);

  return (
    <UserProfileCacheContext.Provider value={contextValue}>
      {children}
    </UserProfileCacheContext.Provider>
  );
};

export const useUserProfileCache = (): UserProfileCacheContextType => {
  const context = useContext(UserProfileCacheContext);
  if (!context) {
    throw new Error('useUserProfileCache must be used within a UserProfileCacheProvider');
  }
  return context;
};

// Hook for getting a specific user's profile with automatic loading
export const useUserProfile = (userId: string | undefined) => {
  const { getUserProfile, refreshUserProfile, isProfileLoading, updateProfileInCache } = useUserProfileCache();

  const profile = userId ? getUserProfile(userId) : null;
  const loading = userId ? isProfileLoading(userId) : false;

  // Auto-load profile if not in cache
  useEffect(() => {
    if (userId && !profile && !loading) {
      refreshUserProfile(userId);
    }
  }, [userId, profile, loading, refreshUserProfile]);

  return {
    profile,
    loading,
    refresh: userId ? () => refreshUserProfile(userId) : () => Promise.resolve(),
    updateCache: userId ? (updates: Partial<UserProfile>) => updateProfileInCache(userId, updates) : () => { }
  };
};

// Hook for getting user display information with automatic fallbacks
export const useUserDisplayInfo = (userId: string | undefined, fallbackName?: string) => {
  const { getUserDisplayName, getUserUsername, getUserAvatar } = useUserProfileCache();
  const { profile, loading } = useUserProfile(userId);

  if (!userId) {
    return {
      displayName: fallbackName || 'Unknown User',
      username: 'user',
      avatar: undefined,
      loading: false
    };
  }

  return {
    displayName: getUserDisplayName(userId, fallbackName),
    username: getUserUsername(userId),
    avatar: getUserAvatar(userId),
    loading
  };
}; 